import React from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { SyncBlockStoreManager } from '@atlaskit/editor-synced-block-provider';
import { SyncBlockError, useFetchSyncBlockData } from '@atlaskit/editor-synced-block-provider';
import { ReactRenderer, type NodeProps } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';

import { SyncedBlockErrorComponent } from './SyncedBlockErrorComponent';
import { SyncedBlockLoadingState } from './SyncedBlockLoadingState';

export interface SyncedBlockProps {
	localId?: string;
	resourceId?: string;
}

export type SyncedBlockNodeProps = NodeProps<SyncedBlockProps>;

export const SyncedBlockNodeComponentRenderer = (
	props: SyncedBlockNodeProps & { syncBlockStoreManager: SyncBlockStoreManager },
): React.JSX.Element => {
	const { resourceId, localId, syncBlockStoreManager } = props;

	const { syncBlockInstance, isLoading } = useFetchSyncBlockData(
		syncBlockStoreManager,
		resourceId,
		localId,
	);

	if (isLoading && !syncBlockInstance) {
		return <SyncedBlockLoadingState />;
	}

	if (syncBlockInstance?.error || !syncBlockInstance?.data) {
		return (
			<SyncedBlockErrorComponent
				error={syncBlockInstance?.error ?? SyncBlockError.Errored}
				resourceId={props.resourceId}
			/>
		);
	}

	const syncBlockDoc = {
		content: syncBlockInstance.data.content,
		version: 1,
		type: 'doc',
	} as DocNode;

	return (
		<RendererActionsContext>
			<div
				data-sync-block
				data-testid="sync-block-renderer-wrapper"
				data-resource-id={props.resourceId}
				data-local-id={props.localId}
			>
				<ReactRenderer
					appearance="full-width"
					adfStage="stage0"
					document={syncBlockDoc}
					disableHeadingIDs={true}
					dataProviders={props.providers}
				/>
			</div>
		</RendererActionsContext>
	);
};
